import { useIntl } from 'react-intl';
import Modal from '../../../../../../../components/Modal';
import PropTypes from 'prop-types';
import styles from './index.module.scss';
import { Divider, Flex, Form } from 'antd';
import { useContext, useEffect, useState } from 'react';
import AccionaButton from '../../../../../../../components/AccionaButton';
import { useDispatch, useSelector } from 'react-redux';
import { useOutletContext } from 'react-router-dom';
import { NotificationsContext } from '../../../../../../../context/NotificationsContext';
import { MainContext } from '../../../../../../../context/MainContext';
import { getConcreteBatches } from '../../../../../../../services/redux/concreteBatch/actions';
import BatchDescriptions from './components/BatchDescriptions';
import BatchForm from './components/BatchForm';
import { isBatchCompleted } from './index.utils';
import useLotification from './hooks/useLotification';
import useNormativeApi from './hooks/useNormativeApi';
import { updateDeliveryNote } from '../../../../../../../services/redux/deliveryNotes/actions';

const { wrapper } = styles;

const EditModal = ({ item, onClose, normative, structuralElements }) => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { setLoading } = useContext(MainContext);
  const { pushNotification } = useContext(NotificationsContext);
  const { project } = useOutletContext();
  const { list: concreteBatches, fetching } = useSelector(
    (state) => state.concreteBatch,
  );
  const [availableBatches, setAvailableBatches] = useState([]);
  const [currentFormValues, setCurrentFormValues] = useState(
    item?.rawData || {},
  );

  const { getProjectStatus } = useNormativeApi();

  const { unBatchItem, createNewConcreteBatch, changeToAnExistingBatch } =
    useLotification();

  const updateItem = ({ deliveryNote, formValues }, isLotification) => {
    setLoading(true);
    if (!formValues.batch_code) {
      dispatch(
        updateDeliveryNote({
          deliveryNote: { ...deliveryNote, ...formValues },
          callback: async () => {
            setLoading(false);
            onClose();
            pushNotification({
              message: intl.formatMessage({
                id: 'pages.quality.normative.traceability.edit.noBatch.notification.success',
              }),
              type: 'success',
            });
          },
        }),
      );
    } else if (formValues.batch_code === 'unbatch') {
      unBatchItem(deliveryNote, formValues, isLotification)
        .then(() => {
          setLoading(false);
          onClose();
          pushNotification({
            message: intl.formatMessage({
              id: 'pages.quality.normative.traceability.edit.unbatch.notification.success',
            }),
            type: 'success',
          });
        })
        .catch((error) => {
          setLoading(false);
          onClose();
          pushNotification({
            message: intl.formatMessage({
              id: 'pages.quality.normative.traceability.edit.unbatch.notification.error',
            }),
            type: 'error',
          });
        });
    } else if (formValues.batch_code === 'newBatch') {
      createNewConcreteBatch(deliveryNote, formValues, isLotification)
        .then(() => {
          setLoading(false);
          onClose();
          pushNotification({
            message: intl.formatMessage({
              id: 'pages.quality.normative.traceability.edit.newBatch.notification.success',
            }),
            type: 'success',
          });
        })
        .catch((error) => {
          console.error(error);
          setLoading(false);
          onClose();
          pushNotification({
            message: intl.formatMessage({
              id: 'pages.quality.normative.traceability.edit.newBatch.notification.error',
            }),
            type: 'error',
          });
        });
    } else {
      changeToAnExistingBatch(deliveryNote, formValues, isLotification)
        .then(() => {
          setLoading(false);
          onClose();
          pushNotification({
            message: intl.formatMessage({
              id: 'pages.quality.normative.traceability.edit.changeBatch.notification.success',
            }),
            type: 'success',
          });
        })
        .catch((error) => {
          console.error(error);
          setLoading(false);
          onClose();
          pushNotification({
            message: intl.formatMessage({
              id: 'pages.quality.normative.traceability.edit.changeBatch.notification.error',
            }),
            type: 'error',
          });
        });
    }
  };

  useEffect(() => {
    const filterKeys = [
      ...(normative === 'coe' ? ['certified_dis'] : []),
      'structural_element',
      'control_type',
      'recipe',
      'plant',
      'concrete_quality',
    ];

    const filters = {};
    filterKeys.forEach((key) => {
      let value = currentFormValues[key] ?? '';
      if (key === 'recipe' && value) value = value.replace('+', '%2B');
      filters[`delivery_note_data[${key}]`] =
        key === 'concrete_quality' || key === 'certified_dis'
          ? value || false
          : value;
    });

    if (Object.values(filters).every((value) => value !== undefined && item)) {
      dispatch(
        getConcreteBatches({
          project: project.id_project,
          filters,
          size: 1000,
        }),
      );
    }
  }, [currentFormValues, dispatch, item, normative, project.id_project]);

  useEffect(() => {
    setCurrentFormValues((prev) => ({ ...prev, ...item?.rawData }));
  }, [item, item?.rawData]);

  useEffect(() => {
    if (concreteBatches) {
      const batchCodeInForm = form.getFieldValue('batch_code');
      const excluded = [];
      const availableOnes = [];

      for (const element of concreteBatches) {
        const isCompleted =
          isBatchCompleted(element) &&
          item?.rawData.mash_control === true &&
          item?.rawData.mash_control_extra !== true;

        const isCurrentBatch = item?.rawData.concrete_batch === element.id;

        const shouldExclude =
          !isCurrentBatch && (isCompleted || item?.rawData.id === element.id);

        if (shouldExclude) {
          excluded.push(element);
          continue;
        }

        const alreadyIncluded = availableOnes.some(
          (y) =>
            element &&
            y.batch_code === element.batch_code &&
            !excluded.includes(element),
        );

        if (!alreadyIncluded) {
          availableOnes.push(element);
        }
      }

      const isSelectedBatchValid = availableOnes.some(
        (batch) => batch.id === batchCodeInForm,
      );

      if (
        !isSelectedBatchValid &&
        batchCodeInForm !== 'unbatch' &&
        batchCodeInForm !== 'newBatch'
      ) {
        form.setFieldsValue({ batch_code: null });
      }

      setAvailableBatches(availableOnes);
    }
  }, [
    concreteBatches,
    form,
    item?.rawData.concrete_batch,
    item?.rawData.id,
    item?.rawData.mash_control,
    item?.rawData.mash_control_extra,
  ]);

  useEffect(() => {
    form.setFieldsValue({
      ...item?.rawData,
      batch_code: item?.rawData.concrete_batch,
    });
  }, [form, item?.rawData]);

  useEffect(() => {
    if (!item) {
      form.resetFields();
    }
  }, [form, item]);

  return (
    <Modal
      open={!!item}
      title={intl
        .formatMessage({
          id: 'pages.quality.normative.traceability.edit.title',
        })
        .toUpperCase()}
      closable
      width="60vw"
      onClose={onClose}
      onCancel={onClose}
    >
      <div className={wrapper}>
        <BatchDescriptions item={item} />
        <Divider />
        <BatchForm
          fetchingBatches={fetching}
          form={form}
          onValuesChange={(values) => {
            setCurrentFormValues((prev) => ({ ...prev, ...values }));
          }}
          structuralElements={structuralElements}
          availableBatches={availableBatches}
          allowUnbatch={Boolean(item?.rawData?.concrete_batch !== null)}
          normative={normative}
          deliveryNote={item?.rawData}
        />
        <Flex justify="space-between" style={{ marginTop: '5em' }}>
          <AccionaButton
            type="default"
            onClick={onClose}
            msg={intl.formatMessage({ id: 'cancel' })}
          />
          <AccionaButton
            type="primary"
            onClick={() => {
              form.validateFields().then((values) => {
                const payload = {
                  deliveryNote: item?.rawData,
                  formValues: values,
                };
                getProjectStatus(payload, true)
                  .then(updateItem(payload, true))
                  .catch();
              });
            }}
            msg={intl.formatMessage({
              id: 'pages.quality.normative.traceability.edit.config.button',
            })}
          />
        </Flex>
      </div>
    </Modal>
  );
};

EditModal.propTypes = {
  normative: PropTypes.oneOf(['coe', 'ehe']).isRequired,
  item: PropTypes.shape({
    docket_number: PropTypes.element,
    plant: PropTypes.string,
    recipe: PropTypes.string,
    delivered: PropTypes.element,
    elements: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    arriving_date: PropTypes.string,
    detailed_location: PropTypes.string,
    structural_element: PropTypes.string,
    control_type: PropTypes.string,
    concreteBatch: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    batchCompliance: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    laboratoryNoteData: PropTypes.shape({
      reference_number: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      compressive_strength_28_1: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      compressive_strength_28_2: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      compressive_strength_28_3: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      compressive_strength_3_1: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      compressive_strength_7_1: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      compressive_strength_60_1: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
    }),
    specimens_number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    rawData: PropTypes.any,
  }),
  onClose: PropTypes.func.isRequired,
  structuralElements: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      structural_element: PropTypes.string,
    }),
  ),
};

EditModal.defaultProps = {
  item: null,
  structuralElements: [],
};

export default EditModal;
